import {
  createVaultScriptHelpers,
  resolveVaultTemplateRecord,
  resolveVaultTemplateString,
  type VaultReferenceContext,
  type VaultReferenceProvider,
} from "./vault";

export type CanonicalAuthType = "none" | "bearer" | "apikey" | "custom";

export interface AuthEditorFields {
  token: string;
  headerName: string;
  apiKey: string;
  customMode: "static" | "script";
  customHeaders: string;
  customScript: string;
}

type NormalizedAuthConfig =
  | { authType: "none"; authConfig: Record<string, never> }
  | { authType: "bearer"; authConfig: { token: string } }
  | { authType: "apikey"; authConfig: { headerName: string; apiKey: string } }
  | { authType: "custom"; authConfig: { mode: "static"; headers: Record<string, string> } | { mode: "script"; script: string } };

export interface AuthTokenCache {
  get: (serverId: string) => string | null;
  set: (serverId: string, token: string) => void;
  invalidate: (serverId: string) => void;
}

export interface AuthHeaderScriptCache {
  get: (serverId: string) => Record<string, string> | null;
  set: (serverId: string, headers: Record<string, string>, expiresAt: number) => void;
  invalidate: (serverId: string) => void;
}

export interface HeaderScriptExecutionResult {
  headers: Record<string, string>;
  expiresAt: number | null;
}

export const DEFAULT_AUTH_EDITOR_FIELDS: AuthEditorFields = {
  token: "",
  headerName: "X-API-Key",
  apiKey: "",
  customMode: "static",
  customHeaders: "{}",
  customScript: "",
};

const EXPIRY_BUFFER_MS = 30_000;
const DEFAULT_TTL_MS = 60_000;
const SCRIPT_TIMEOUT_MS = 10_000;

function parseJsonObject(value: string | undefined | null): Record<string, unknown> | null {
  if (!value) return null;

  try {
    const parsed = JSON.parse(value);
    return typeof parsed === "object" && parsed !== null ? parsed as Record<string, unknown> : null;
  } catch {
    return null;
  }
}

function normalizeHeaders(input: unknown): Record<string, string> {
  const headers: Record<string, string> = {};
  if (!input || typeof input !== "object") return headers;

  for (const [key, value] of Object.entries(input)) {
    const normalizedKey = key.trim();
    if (!normalizedKey) continue;
    if (typeof value === "string") {
      headers[normalizedKey] = value;
      continue;
    }
    if (value != null) {
      headers[normalizedKey] = String(value);
    }
  }

  return headers;
}

function wrapLegacyBearerScript(tokenScript: string): string {
  const trimmed = tokenScript.trim();
  if (!trimmed) return "";
  return `const token = await (async () => {\n${trimmed}\n})();\nif (typeof token !== "string" || !token.trim()) {\n  throw new Error("Legacy token script must return a non-empty string");\n}\nreturn { Authorization: \`Bearer \${token.trim()}\` };`;
}

export function normalizeStoredAuth(
  authType: string,
  authConfig: string | undefined | null,
): NormalizedAuthConfig {
  const parsed = parseJsonObject(authConfig);

  switch (authType) {
    case "script":
      return {
        authType: "custom",
        authConfig: {
          mode: "script",
          script: wrapLegacyBearerScript(
            typeof parsed?.tokenScript === "string"
            ? parsed.tokenScript
            : typeof parsed?.command === "string"
              ? parsed.command
              : "",
          ),
        },
      };

    case "bearer":
      if (parsed?.mode === "script" || parsed?.useScript === true) {
        return {
          authType: "custom",
          authConfig: {
            mode: "script",
            script: wrapLegacyBearerScript(typeof parsed?.tokenScript === "string" ? parsed.tokenScript : ""),
          },
        };
      }
      return {
        authType: "bearer",
        authConfig: {
          token: typeof parsed?.token === "string" ? parsed.token : "",
        },
      };

    case "apikey":
      return {
        authType: "apikey",
        authConfig: {
          headerName: typeof parsed?.headerName === "string"
            ? parsed.headerName
            : typeof parsed?.header === "string"
              ? parsed.header
              : DEFAULT_AUTH_EDITOR_FIELDS.headerName,
          apiKey: typeof parsed?.apiKey === "string"
            ? parsed.apiKey
            : typeof parsed?.key === "string"
              ? parsed.key
              : "",
        },
      };

    case "custom": {
      if (parsed?.mode === "script") {
        return {
          authType: "custom",
          authConfig: {
            mode: "script",
            script: typeof parsed.script === "string" ? parsed.script : "",
          },
        };
      }
      const source = parsed && "headers" in parsed ? parsed.headers : parsed;
      return {
        authType: "custom",
        authConfig: {
          mode: "static",
          headers: normalizeHeaders(source),
        },
      };
    }

    default:
      return {
        authType: "none",
        authConfig: {},
      };
  }
}

export function getCanonicalAuthType(
  authType: string,
  authConfig?: string | undefined | null,
): CanonicalAuthType {
  return normalizeStoredAuth(authType, authConfig).authType;
}

export function parseStoredAuthToEditorFields(
  authType: string,
  authConfig: string | undefined | null,
): AuthEditorFields {
  const normalized = normalizeStoredAuth(authType, authConfig);

  switch (normalized.authType) {
    case "bearer":
      return {
        ...DEFAULT_AUTH_EDITOR_FIELDS,
        token: normalized.authConfig.token,
      };

    case "apikey":
      return {
        ...DEFAULT_AUTH_EDITOR_FIELDS,
        headerName: normalized.authConfig.headerName,
        apiKey: normalized.authConfig.apiKey,
      };

    case "custom":
      return {
        ...DEFAULT_AUTH_EDITOR_FIELDS,
        customMode: normalized.authConfig.mode,
        ...(normalized.authConfig.mode === "static"
          ? { customHeaders: JSON.stringify({ headers: normalized.authConfig.headers }) }
          : { customScript: normalized.authConfig.script }),
      };

    case "none":
      return { ...DEFAULT_AUTH_EDITOR_FIELDS };
  }
}

export function serializeAuthConfig(
  authType: CanonicalAuthType,
  fields: AuthEditorFields,
): string {
  switch (authType) {
    case "bearer":
      return JSON.stringify({ token: fields.token });

    case "apikey":
      return JSON.stringify({
        headerName: fields.headerName,
        apiKey: fields.apiKey,
      });

    case "custom": {
      if (fields.customMode === "script") {
        return JSON.stringify({
          mode: "script",
          script: fields.customScript,
        });
      }
      const parsed = parseJsonObject(fields.customHeaders);
      const source = parsed && "headers" in parsed ? parsed.headers : parsed;
      return JSON.stringify({
        mode: "static",
        headers: normalizeHeaders(source),
      });
    }

    case "none":
      return "{}";
  }
}

export function parseJwtExp(token: string): number | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]!.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function createAuthTokenCache(): AuthTokenCache {
  const cache = new Map<string, { token: string; expiresAt: number }>();

  return {
    get(serverId) {
      const entry = cache.get(serverId);
      if (!entry) return null;
      if (Date.now() >= entry.expiresAt - EXPIRY_BUFFER_MS) {
        cache.delete(serverId);
        return null;
      }
      return entry.token;
    },
    set(serverId, token) {
      const jwtExp = parseJwtExp(token);
      cache.set(serverId, {
        token,
        expiresAt: jwtExp ?? Date.now() + DEFAULT_TTL_MS,
      });
    },
    invalidate(serverId) {
      cache.delete(serverId);
    },
  };
}

export const authTokenCache = createAuthTokenCache();

export function createAuthHeaderScriptCache(): AuthHeaderScriptCache {
  const cache = new Map<string, { headers: Record<string, string>; expiresAt: number }>();

  return {
    get(serverId) {
      const entry = cache.get(serverId);
      if (!entry) return null;
      if (Date.now() >= entry.expiresAt - EXPIRY_BUFFER_MS) {
        cache.delete(serverId);
        return null;
      }
      return entry.headers;
    },
    set(serverId, headers, expiresAt) {
      cache.set(serverId, {
        headers,
        expiresAt,
      });
    },
    invalidate(serverId) {
      cache.delete(serverId);
    },
  };
}

export const authHeaderScriptCache = createAuthHeaderScriptCache();

async function executeScriptWithTimeout<T>(
  script: string,
  bindings: Record<string, unknown>,
): Promise<T> {
  const AsyncFunction = Object.getPrototypeOf(async function () {}).constructor;
  const bindingNames = Object.keys(bindings);
  const bindingValues = Object.values(bindings);
  const fn = new AsyncFunction(...bindingNames, script) as (...args: unknown[]) => Promise<unknown>;

  const result = await Promise.race([
    fn(...bindingValues),
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("Script timed out (10s)")), SCRIPT_TIMEOUT_MS),
    ),
  ]);

  return result as T;
}

export async function executeTokenScript(
  script: string,
  provider?: VaultReferenceProvider,
  context?: VaultReferenceContext,
): Promise<string> {
  const { secret, config } = createVaultScriptHelpers(provider, context);
  const result = await executeScriptWithTimeout<unknown>(script, { secret, config, fetch: globalThis.fetch });

  if (typeof result !== "string" || !result.trim()) {
    throw new Error("Token script must return a non-empty string");
  }

  return result.trim();
}

function parseScriptExpiry(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value < 1_000_000_000_000 ? value * 1000 : value;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    if (/^\d+$/.test(trimmed)) {
      const numeric = Number(trimmed);
      return parseScriptExpiry(numeric);
    }

    const parsed = Date.parse(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function inferHeaderScriptExpiry(headers: Record<string, string>): number | null {
  const authorization = headers.Authorization?.trim();
  if (!authorization?.toLowerCase().startsWith("bearer ")) return null;

  const token = authorization.slice("Bearer ".length).trim();
  return parseJwtExp(token);
}

function normalizeHeaderScriptResult(result: unknown): HeaderScriptExecutionResult {
  if (!result || typeof result !== "object") {
    throw new Error("Header script must return an object");
  }

  const record = result as Record<string, unknown>;
  const hasWrappedShape = "headers" in record || "expiresAt" in record;
  const headers = hasWrappedShape ? normalizeHeaders(record.headers) : normalizeHeaders(record);
  const explicitExpiry = hasWrappedShape ? parseScriptExpiry(record.expiresAt) : null;

  return {
    headers,
    expiresAt: explicitExpiry ?? inferHeaderScriptExpiry(headers),
  };
}

export async function executeHeaderScript(
  script: string,
  provider?: VaultReferenceProvider,
  context?: VaultReferenceContext,
): Promise<HeaderScriptExecutionResult> {
  const { secret, config } = createVaultScriptHelpers(provider, context);
  const result = await executeScriptWithTimeout<unknown>(script, { secret, config, fetch: globalThis.fetch });
  return normalizeHeaderScriptResult(result);
}

export async function buildStoredAuthHeaders(params: {
  serverId?: string;
  authType: string;
  authConfig?: string | null;
  executeTokenScript?: (script: string) => Promise<string>;
  executeHeaderScript?: (script: string) => Promise<HeaderScriptExecutionResult>;
  tokenCache?: AuthTokenCache;
  headerScriptCache?: AuthHeaderScriptCache;
  vaultProvider?: VaultReferenceProvider;
  vaultContext?: VaultReferenceContext;
}): Promise<Record<string, string>> {
  const normalized = normalizeStoredAuth(params.authType, params.authConfig);

  switch (normalized.authType) {
    case "none":
      return {};

    case "bearer":
      const token = (await resolveVaultTemplateString(
        normalized.authConfig.token,
        params.vaultProvider,
        params.vaultContext,
      )).trim();
      return token ? { Authorization: `Bearer ${token}` } : {};

    case "apikey": {
      const headerName = (
        await resolveVaultTemplateString(
          normalized.authConfig.headerName,
          params.vaultProvider,
          params.vaultContext,
        )
      ).trim();
      const apiKey = await resolveVaultTemplateString(
        normalized.authConfig.apiKey,
        params.vaultProvider,
        params.vaultContext,
      );
      return headerName && apiKey ? { [headerName]: apiKey } : {};
    }

    case "custom":
      if (normalized.authConfig.mode === "script") {
        if (!normalized.authConfig.script.trim()) {
          return {};
        }
        if (params.serverId && params.headerScriptCache) {
          const cachedHeaders = params.headerScriptCache.get(params.serverId);
          if (cachedHeaders) {
            return cachedHeaders;
          }
        }
        if (!params.executeHeaderScript) {
          throw new Error("Header script auth requires a header script executor");
        }
        const result = await params.executeHeaderScript(normalized.authConfig.script);
        if (params.serverId && params.headerScriptCache) {
          if (result.expiresAt) {
            params.headerScriptCache.set(params.serverId, result.headers, result.expiresAt);
          } else {
            params.headerScriptCache.invalidate(params.serverId);
          }
        }
        return result.headers;
      }

      return resolveVaultTemplateRecord(
        normalized.authConfig.headers,
        params.vaultProvider,
        params.vaultContext,
      );
  }
}
